import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { signupApi, customerApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'
import Input from '../components/common/Input'

/**
 * Magic-link approval page. URL: /approve/:token
 *
 * Loads the signup request by its token (public — anyone with the link can
 * view) and, if the visitor is a logged-in sysadmin, lets them approve or
 * reject.
 *
 * The UI varies by account type:
 *   - HOMEGROUP: shows customer-assignment radio (keep/existing/new), lit
 *     rep / GSR name override fields. Same complex flow as before.
 *   - PERSONAL: simple yes-or-no. The system will auto-create a customer
 *     named "{user name} — Personal" of kind='individual'. No overrides.
 */
export default function ApproveSignup() {
    const { token } = useParams()
    const navigate = useNavigate()
    const { user, loading: authLoading, isSysAdmin } = useAuth()

    const [request, setRequest] = useState(null)
    const [customers, setCustomers] = useState([])
    const [loading, setLoading] = useState(true)
    const [notFound, setNotFound] = useState(false)

    // Approval form state (homegroup only)
    const [overrideMode, setOverrideMode] = useState('keep')   // keep | existing | new
    const [overrideCustomerId, setOverrideCustomerId] = useState('')
    const [overrideName, setOverrideName] = useState('')
    const [overrideKind, setOverrideKind] = useState('homegroup')
    const [overrideLitRepName, setOverrideLitRepName] = useState('')
    const [overrideGsrName, setOverrideGsrName] = useState('')
    const [busy, setBusy] = useState(false)

    // Reject form state
    const [rejecting, setRejecting] = useState(false)
    const [rejectReason, setRejectReason] = useState('')

    useEffect(() => {
        load()
    }, [token])

    async function load() {
        setLoading(true)
        try {
            const [reqRes, custRes] = await Promise.all([
                signupApi.getByToken(token),
                customerApi.list(false).catch(() => ({ success: false, customers: [] })),
            ])
            if (reqRes.success) {
                setRequest(reqRes.request)
                if (reqRes.request.isNewCustomerRequested && reqRes.request.customerName) {
                    setOverrideName(reqRes.request.customerName)
                    setOverrideKind(reqRes.request.customerKind || 'homegroup')
                }
                setOverrideLitRepName(reqRes.request.litRepName || '')
                setOverrideGsrName(reqRes.request.gsrName || '')
            } else {
                setNotFound(true)
            }
            if (custRes.success) setCustomers(custRes.customers)
        } catch {
            setNotFound(true)
        } finally {
            setLoading(false)
        }
    }

    async function handleApprove() {
        const isPersonal = request?.accountType === 'personal'
        const body = {}

        if (!isPersonal) {
            // Homegroup approval: build override body
            if (overrideMode === 'existing') {
                if (!overrideCustomerId) {
                    toast.error('Please pick a customer')
                    return
                }
                body.customerId = Number(overrideCustomerId)
            } else if (overrideMode === 'new') {
                if (!overrideName.trim()) {
                    toast.error('Please enter the new customer name')
                    return
                }
                body.overrideCustomerName = overrideName.trim()
                body.overrideCustomerKind = overrideKind
            }

            if (overrideLitRepName !== (request.litRepName || '')) {
                body.overrideLitRepName = overrideLitRepName.trim() || null
            }
            if (overrideGsrName !== (request.gsrName || '')) {
                body.overrideGsrName = overrideGsrName.trim() || null
            }
        }
        // For personal: empty body — server ignores any overrides and auto-creates

        setBusy(true)
        try {
            const response = await signupApi.approve(token, body)
            if (response.success) {
                toast.success(`${request.name} approved`)
                navigate('/admin/signups')
            } else {
                toast.error(response.message || 'Approval failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    async function handleReject() {
        setBusy(true)
        try {
            const response = await signupApi.reject(token, { reason: rejectReason || null })
            if (response.success) {
                toast.success(`${request.name}'s request rejected`)
                navigate('/admin/signups')
            } else {
                toast.error(response.message || 'Rejection failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    // ----- Loading / not found / waiting on auth -----------------------------

    if (loading || authLoading) {
        return <Shell><div className="text-center text-gray-500">Loading...</div></Shell>
    }

    if (notFound) {
        return (
            <Shell>
                <div className="text-center space-y-3">
                    <h2 className="text-xl font-semibold text-ghasc-navy">Request not found</h2>
                    <p className="text-gray-600 text-sm">
                        This request may have already been decided, or the link may be invalid.
                    </p>
                    <Button variant="secondary" onClick={() => navigate('/')}>Home</Button>
                </div>
            </Shell>
        )
    }

    // ----- Logged-out visitor → ask them to log in --------------------------

    if (!user) {
        return (
            <Shell>
                <h2 className="text-xl font-semibold text-ghasc-navy mb-4">Signup request</h2>
                <RequestSummary request={request} />
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                    Please log in as the literature chair to approve or reject this request.
                </div>
                <div className="mt-4 text-center">
                    <Link
                        to={`/login?next=${encodeURIComponent(`/approve/${token}`)}`}
                        className="inline-block bg-ghasc-navy text-white px-4 py-2 rounded-lg hover:bg-ghasc-navy-light"
                    >
                        Log in
                    </Link>
                </div>
            </Shell>
        )
    }

    // ----- Logged in but not sysadmin ----------------------------------------

    if (!isSysAdmin) {
        return (
            <Shell>
                <h2 className="text-xl font-semibold text-ghasc-navy mb-4">Signup request</h2>
                <RequestSummary request={request} />
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                    Only the literature chair can approve signup requests.
                </div>
                <div className="mt-4 text-center">
                    <Button variant="secondary" onClick={() => navigate('/')}>Back to dashboard</Button>
                </div>
            </Shell>
        )
    }

    // ----- The actual approve/reject UI --------------------------------------

    const isPersonal = request.accountType === 'personal'
    const personalCustomerName = `${request.name} — Personal`

    return (
        <Shell wide>
            <h2 className="text-xl font-semibold text-ghasc-navy mb-4">Review signup request</h2>
            <RequestSummary request={request} />

            {!rejecting ? (
                <>
                    {/* PERSONAL: simple yes-or-no */}
                    {isPersonal && (
                        <div className="mt-6 border border-emerald-200 bg-emerald-50 rounded-lg p-4">
                            <h3 className="font-medium text-emerald-900 mb-2">Personal account</h3>
                            <p className="text-sm text-emerald-900">
                                Approving will create a new customer named{' '}
                                <strong>"{personalCustomerName}"</strong> (kind: individual) and link
                                this user to it. They'll be able to shop and check out, but won't have
                                access to homegroup-specific features like GSR reports.
                            </p>
                        </div>
                    )}

                    {/* HOMEGROUP: existing override UI */}
                    {!isPersonal && (
                        <>
                            <div className="mt-6 border border-gray-200 rounded-lg p-4 space-y-4">
                                <h3 className="font-medium text-gray-900">Customer assignment</h3>
                                <p className="text-sm text-gray-600 -mt-2">
                                    By default, this user is filed under the homegroup they
                                    {request.isNewCustomerRequested ? ' requested.' : ' picked.'}
                                    You can override that here.
                                </p>

                                <div className="space-y-2">
                                    <RadioRow
                                        label={
                                            request.isNewCustomerRequested
                                                ? `Use as requested: create "${request.customerName}" (${request.customerKind || 'homegroup'})`
                                                : `Use as picked: ${request.customerName || 'unknown customer'}`
                                        }
                                        checked={overrideMode === 'keep'}
                                        onChange={() => setOverrideMode('keep')}
                                    />
                                    <RadioRow
                                        label="Pick a different existing customer"
                                        checked={overrideMode === 'existing'}
                                        onChange={() => setOverrideMode('existing')}
                                    />
                                    {overrideMode === 'existing' && (
                                        <select
                                            value={overrideCustomerId}
                                            onChange={(e) => setOverrideCustomerId(e.target.value)}
                                            className="ml-6 w-[calc(100%-1.5rem)] px-3 py-2 border border-gray-300 rounded-lg"
                                        >
                                            <option value="">— Choose customer —</option>
                                            {customers.filter(c => c.kind !== 'individual').map(c => (
                                                <option key={c.id} value={c.id}>{c.name} ({c.kind})</option>
                                            ))}
                                        </select>
                                    )}
                                    <RadioRow
                                        label={
                                            request.isNewCustomerRequested
                                                ? "Create a new customer with corrected name/kind"
                                                : "Create a new customer (overriding their existing pick)"
                                        }
                                        checked={overrideMode === 'new'}
                                        onChange={() => setOverrideMode('new')}
                                    />
                                    {overrideMode === 'new' && (
                                        <div className="ml-6 space-y-2">
                                            <Input
                                                id="override-name"
                                                label="Customer name"
                                                value={overrideName}
                                                onChange={(e) => setOverrideName(e.target.value)}
                                            />
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Kind</label>
                                                <select
                                                    value={overrideKind}
                                                    onChange={(e) => setOverrideKind(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                    <option value="homegroup">Homegroup</option>
                                                    <option value="subcommittee">Subcommittee</option>
                                                    <option value="area">Area</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <NameOverridesSection
                                request={request}
                                overrideLitRepName={overrideLitRepName}
                                setOverrideLitRepName={setOverrideLitRepName}
                                overrideGsrName={overrideGsrName}
                                setOverrideGsrName={setOverrideGsrName}
                            />
                        </>
                    )}

                    <div className="flex justify-between gap-2 pt-4 mt-4 border-t border-gray-200">
                        <Button variant="secondary" onClick={() => setRejecting(true)} disabled={busy}>
                            Reject...
                        </Button>
                        <Button onClick={handleApprove} loading={busy}>
                            Approve
                        </Button>
                    </div>
                </>
            ) : (
                <div className="mt-6 space-y-3">
                    <h3 className="font-medium text-gray-900">Reject signup</h3>
                    <Input
                        id="reject-reason"
                        label="Reason (optional, included in the email to the user)"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                    />
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="secondary" onClick={() => setRejecting(false)} disabled={busy}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleReject} loading={busy}>
                            Reject &amp; email user
                        </Button>
                    </div>
                </div>
            )}
        </Shell>
    )
}

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------
function RequestSummary({ request }) {
    const isPersonal = request.accountType === 'personal'
    return (
        <div className="bg-gray-50 rounded-lg p-4 space-y-1.5 text-sm">
            <div className="flex">
                <span className="w-32 text-gray-500">Account type</span>
                <span className="flex-1">
                    {isPersonal ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                            Personal
                        </span>
                    ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Homegroup
                        </span>
                    )}
                </span>
            </div>
            <Field label="Name" value={request.name} />
            <Field label="Email" value={request.email} />
            {request.phone && <Field label="Phone" value={request.phone} />}
            {request.city && <Field label="City" value={request.city} />}
            {!isPersonal && request.litRepName && <Field label="Lit Rep" value={request.litRepName} />}
            {!isPersonal && request.gsrName && <Field label="GSR" value={request.gsrName} />}
            {!isPersonal && (
                <Field
                    label={request.isNewCustomerRequested ? 'Requested homegroup' : 'Picked homegroup'}
                    value={
                        request.isNewCustomerRequested
                            ? `${request.customerName} — needs creation`
                            : request.customerName
                    }
                />
            )}
            <Field
                label="Submitted"
                value={new Date(request.createdAt).toLocaleString()}
            />
        </div>
    )
}

function Field({ label, value }) {
    return (
        <div className="flex">
            <span className="w-32 text-gray-500">{label}</span>
            <span className="flex-1 text-gray-900">{value || <em className="text-gray-400">—</em>}</span>
        </div>
    )
}

function RadioRow({ label, checked, onChange }) {
    return (
        <label className="flex items-start text-sm cursor-pointer">
            <input type="radio" checked={checked} onChange={onChange} className="mr-2 mt-1" />
            <span>{label}</span>
        </label>
    )
}

function NameOverridesSection({ request, overrideLitRepName, setOverrideLitRepName, overrideGsrName, setOverrideGsrName }) {
    return (
        <div className="mt-4 border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="font-medium text-gray-900">Lit Rep / GSR names</h3>
            <p className="text-sm text-gray-600 -mt-2">
                These prefill from the signup form. Edit here if needed before approval.
                Leave blank to clear. If a GSR name is set, the user gets the GSR role
                automatically.
            </p>
            <Input
                id="approve-lit-rep"
                label="Lit Rep Name"
                value={overrideLitRepName}
                onChange={(e) => setOverrideLitRepName(e.target.value)}
                placeholder="(none)"
            />
            <Input
                id="approve-gsr"
                label="GSR Name"
                value={overrideGsrName}
                onChange={(e) => setOverrideGsrName(e.target.value)}
                placeholder="(none)"
            />
        </div>
    )
}

function Shell({ children, wide }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ghasc-navy-dark to-ghasc-navy px-4 py-8">
            <div className={`bg-white rounded-2xl shadow-xl p-8 w-full ${wide ? 'max-w-2xl' : 'max-w-md'}`}>
                <div className="text-center mb-6">
                    <h1 className="text-xl font-bold text-ghasc-navy">GHASC Literature</h1>
                </div>
                {children}
            </div>
        </div>
    )
}